import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { HPEvent, OutboundEmail, Registration, RegistrationStatus } from '../types';
import { getEventById } from '../data/events';
const STORAGE_KEY = 'hp-events-registrations-v2';
const EMAIL_KEY = 'hp-events-outbox-v2';
export interface RegistrationInput {
  eventId: string;
  fullName: string;
  email: string;
  organisation: string;
  jobTitle: string;
  profile: string;
  intent: string;
  dietary?: string;
}
interface RegistrationsValue {
  registrations: Registration[];
  outbox: OutboundEmail[];
  registrationsForEvent: (eventId: string) => Registration[];
  submitRegistration: (input: RegistrationInput) => Registration;
  decide: (id: string, status: Exclude<RegistrationStatus, 'pending'>, note?: string) => void;
  decideMany: (ids: string[], status: Exclude<RegistrationStatus, 'pending'>) => void;
  resendTicket: (id: string) => void;
  reset: () => void;
}
const RegistrationsContext = createContext<RegistrationsValue | null>(null);
function readStore<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}
function randomSuffix(): string {
  const alphabet = 'ACDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 4; i += 1) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}
function makeId(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}
const seedRegistrations: Registration[] = [{
  id: 'regseedtest',
  ticketCode: 'HPGIIN7XQP',
  eventId: 'giin-amsterdam-2026',
  fullName: 'Jonathan Mzn',
  email: 'jonathan@mzninternational.com',
  organisation: 'MZN International',
  jobTitle: 'Managing Director',
  profile: 'Impact investor or financial sector',
  intent: 'Test registration for the Amsterdam programme. Reviewing the confirmation flow and the ticket that arrives on approval.',
  submittedAt: '2026-07-28T10:04:00.000Z',
  status: 'pending'
}, {
  id: 'regseed1',
  ticketCode: 'HPGIIN4KQ2',
  eventId: 'giin-amsterdam-2026',
  fullName: 'Amina Al Rashid',
  email: 'a.alrashid@gulfsovereignfund.ae',
  organisation: 'Gulf Sovereign Fund',
  jobTitle: 'Head of Sustainable Investments',
  profile: 'Impact investor or financial sector',
  intent: 'Exploring blended finance vehicles for regional climate adaptation.',
  submittedAt: '2026-07-21T09:12:00.000Z',
  status: 'pending'
}, {
  id: 'regseed2',
  ticketCode: 'HPGIIN8TRB',
  eventId: 'giin-amsterdam-2026',
  fullName: 'Jonas Meier',
  email: 'jonas.meier@dfpartners.de',
  organisation: 'Development Finance Partners',
  jobTitle: 'Director, Climate Portfolio',
  profile: 'Government, multilateral or donor',
  intent: 'Interested in the NGO pitch block and the co investment pipeline that follows it.',
  dietary: 'Vegetarian',
  submittedAt: '2026-07-22T14:40:00.000Z',
  status: 'approved',
  decidedAt: '2026-07-23T08:02:00.000Z',
  reviewer: 'Partner review'
}, {
  id: 'regseed3',
  ticketCode: 'HPAVPN2HFK',
  eventId: 'avpn-2026',
  fullName: 'Wei Ling Tan',
  email: 'weiling.tan@asiaimpact.sg',
  organisation: 'Asia Impact Capital',
  jobTitle: 'Head of Capital Mobilisation',
  profile: 'Impact investor or financial sector',
  intent: 'Speaking on the regional capital panel and bringing two colleagues from the deployment team.',
  submittedAt: '2026-07-25T08:47:00.000Z',
  status: 'pending'
}, {
  id: 'regseed4',
  ticketCode: 'HPWEFD9QLD',
  eventId: 'wef-davos-2027',
  fullName: 'Marcus Bell',
  email: 'marcus.bell@freemail.com',
  organisation: 'Independent',
  jobTitle: 'Consultant',
  profile: 'Other',
  intent: 'Would like to attend and network during the Annual Meeting week.',
  submittedAt: '2026-07-24T16:22:00.000Z',
  status: 'pending'
}];
const seedOutbox: OutboundEmail[] = [{
  id: 'mailseedtest',
  kind: 'received',
  to: 'jonathan@mzninternational.com',
  toName: 'Jonathan Mzn',
  subject: 'Request received, Human Planet at the GIIN Impact Forum',
  preheader: 'Your request is with our team for review.',
  sentAt: '2026-07-28T10:04:00.000Z',
  registrationId: 'regseedtest',
  eventId: 'giin-amsterdam-2026'
}];
function ticketFor(event: HPEvent | undefined): string {
  return `HP${event ? event.code : 'GEN'}${randomSuffix()}`;
}
export function RegistrationsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [registrations, setRegistrations] = useState<Registration[]>(() => readStore<Registration[]>(STORAGE_KEY, seedRegistrations));
  const [outbox, setOutbox] = useState<OutboundEmail[]>(() => readStore<OutboundEmail[]>(EMAIL_KEY, seedOutbox));
  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(registrations));
  }, [registrations]);
  useEffect(() => {
    window.localStorage.setItem(EMAIL_KEY, JSON.stringify(outbox));
  }, [outbox]);
  const pushEmail = useCallback((email: OutboundEmail) => {
    setOutbox(prev => [email, ...prev]);
  }, []);
  const emailForDecision = useCallback((registration: Registration, status: Exclude<RegistrationStatus, 'pending'>) => {
    const event = getEventById(registration.eventId);
    const eventTitle = event ? event.title : 'Human Planet event';
    if (status === 'approved') {
      pushEmail({
        id: makeId('mail'),
        kind: 'ticket',
        to: registration.email,
        toName: registration.fullName,
        subject: `Your ticket, ${eventTitle}`,
        preheader: `Approved. Ticket ${registration.ticketCode} is attached as a PDF.`,
        sentAt: new Date().toISOString(),
        registrationId: registration.id,
        eventId: registration.eventId,
        attachmentName: `${registration.ticketCode}.pdf`
      });
    } else {
      pushEmail({
        id: makeId('mail'),
        kind: 'declined',
        to: registration.email,
        toName: registration.fullName,
        subject: `Update on your registration, ${eventTitle}`,
        preheader: 'We are unable to confirm a place for you at this event.',
        sentAt: new Date().toISOString(),
        registrationId: registration.id,
        eventId: registration.eventId
      });
    }
  }, [pushEmail]);
  const submitRegistration = useCallback((input: RegistrationInput) => {
    const event = getEventById(input.eventId);
    const registration: Registration = {
      id: makeId('reg'),
      ticketCode: ticketFor(event),
      submittedAt: new Date().toISOString(),
      status: 'pending',
      ...input
    };
    setRegistrations(prev => [registration, ...prev]);
    pushEmail({
      id: makeId('mail'),
      kind: 'received',
      to: registration.email,
      toName: registration.fullName,
      subject: `Request received, ${event ? event.title : 'Human Planet event'}`,
      preheader: 'Your request is with our team for review. We reply within two working days.',
      sentAt: new Date().toISOString(),
      registrationId: registration.id,
      eventId: registration.eventId
    });
    return registration;
  }, [pushEmail]);
  const decide = useCallback((id: string, status: Exclude<RegistrationStatus, 'pending'>, note?: string) => {
    setRegistrations(prev => prev.map(registration => {
      if (registration.id !== id) return registration;
      const updated: Registration = {
        ...registration,
        status,
        note: note ?? registration.note,
        decidedAt: new Date().toISOString(),
        reviewer: 'Partner review'
      };
      emailForDecision(updated, status);
      return updated;
    }));
  }, [emailForDecision]);
  const decideMany = useCallback((ids: string[], status: Exclude<RegistrationStatus, 'pending'>) => {
    setRegistrations(prev => prev.map(registration => {
      if (!ids.includes(registration.id) || registration.status === status) return registration;
      const updated: Registration = {
        ...registration,
        status,
        decidedAt: new Date().toISOString(),
        reviewer: 'Partner review'
      };
      emailForDecision(updated, status);
      return updated;
    }));
  }, [emailForDecision]);
  const resendTicket = useCallback((id: string) => {
    const registration = registrations.find(item => item.id === id);
    if (!registration || registration.status !== 'approved') return;
    emailForDecision(registration, 'approved');
  }, [registrations, emailForDecision]);
  const registrationsForEvent = useCallback((eventId: string) => registrations.filter(registration => registration.eventId === eventId), [registrations]);
  const reset = useCallback(() => {
    setRegistrations(seedRegistrations);
    setOutbox(seedOutbox);
  }, []);
  const value = useMemo<RegistrationsValue>(() => ({
    registrations,
    outbox,
    registrationsForEvent,
    submitRegistration,
    decide,
    decideMany,
    resendTicket,
    reset
  }), [registrations, outbox, registrationsForEvent, submitRegistration, decide, decideMany, resendTicket, reset]);
  return <RegistrationsContext.Provider value={value} data-id="element-213" data-mp="36ji3e2dhvurd">{children}</RegistrationsContext.Provider>;
}
export function useRegistrations(): RegistrationsValue {
  const context = useContext(RegistrationsContext);
  if (!context) {
    throw new Error('useRegistrations must be used within a RegistrationsProvider');
  }
  return context;
}